import { OBJECT_STRIDE } from "./ObjectUniforms";
import { ObjectUniformBuffer } from './ObjectUniformBuffer';
import { readFragmentFlagsGPU } from "./ObjectUniforms";

// Constant used in lookup table to indicate that a fragment is not on GPU.
const NotOnGPU = -1;

// TODO: This is chosen arbitrarily, but seems to give good enough results for now
const MASS_UPDATE_THRESHOLD = 1000;

const FragmentChangeType = {
  Material: 1,
  Transform: 2,
  Theming: 3,
  Highlighting: 4,
  Full: 5
};

// A ModelUniformUpdater monitors change events on a single model
// and makes sure that the uniform buffers on GPU are correspondingly updated.
//
// This includes changes on...
//   - FragmentList:  E.g. setting a material, transform, or theming color of a fragment
//   - RenderBatches: Order changes after sorting
//   - Iterator:      Changing from LinearIterator to BVHIterator (which brings different batches)
export class ModelUniformUpdater {

  // Given a fragId, this allows us to find where the uniforms for this fragment are stored in the GPU-side UniformBuffers.
  // May contain NotOnGPU if there is nothing allocated on GPU for a fragment.
  /** type {Int32Array} */
  #fragIdToItemIndex; // At which position is it within this buffer.

  /** {FragmentList} */
  #fragmentList;
  /** {Model} */
  #model;

  // Currently used whenever we have to enumerate all RenderBatches of a model.
  // Should be replaced by a more scalable solution that is more dynamic and only knows about things on GPU.
  /** ModelIteratorBVH|ModelIteratorLinear */
  #iterator;

  // Manages all object uniform buffers
  /** @type {ObjectUniformBuffer} */
  #uniforms;

  // Bound callback functions
  #boundIteratorChangedCallback = this.#iteratorChangedCallback.bind(this);
  #boundFragOrderChangedCallback = this.#fragOrderChangedCallback.bind(this);
  #boundMeshSetCallback = this.setOneFragment.bind(this);
  #boundTransformChangedCallback = this.updateTransform.bind(this);
  #boundModelMatrixChangedCallback = this.onModelMatrixChanged.bind(this);
  #boundMaterialChangedCallback = this.setMaterial.bind(this);
  #boundThemingColorChangedCallback = this.onThemingColorChanged.bind(this);
  #boundHighlightingCategoryChangedCallback = this.#markScenesDirty.bind(this);
  #boundHighlightingChangedCallback = this.setHighlighting.bind(this);

  // Indicates that many fragments are updated simultaneously. We stop updating individual uniforms in this case.
  #massUpdate = false;
  #updateCount = 0;

  // Needs to be notified if changes require a re-recoding of RenderBundles
  #renderer;

  // To disable theming, the color is set to all zeroes.
  #noThemingColor = new THREE.Vector4(0, 0, 0, 0);

  /**
   * @param {Model}               model
   * @param {ObjectUniformBuffer} uniforms
   * @param {Renderer}            renderer
   */
  constructor(model, uniforms, renderer) {

    this.#model = model;
    this.#renderer = renderer;

    // Listen to fragment data changes
    this.#fragmentList = model.getFragmentList();
    this.#registerFragmentListCallbacks();

    // Init lookup tables
    const fragCount = this.#fragmentList.getCount();
    this.#fragIdToItemIndex = new Int32Array(fragCount);
    this.#clearFragmentLookup(); // init all with 'NotOnGPU'

    this.#uniforms = uniforms;

    // Listen to iterator changes
    model.registerIteratorChangedCallback(this.#boundIteratorChangedCallback);

    // The model is already initialized at this point, so the initial iterator is set and we need to query it.
    this.#iteratorChangedCallback(this.#model.getIterator());
  }

  dtor() {
    // Remove callbacks
    this.#model.removeIteratorChangedCallback(this.#boundIteratorChangedCallback);
    this.#clearFragmentListCallbacks();
    this.#clearRenderbatchCallbacks();
  }

  #registerFragmentListCallbacks() {
    this.#fragmentList.registerMeshSetCallback(this.#boundMeshSetCallback);
    this.#fragmentList.registerTransformChangedCallback(this.#boundTransformChangedCallback);
    this.#fragmentList.registerMaterialChangedCallback(this.#boundMaterialChangedCallback);
    this.#fragmentList.registerThemingColorChangedCallback(this.#boundThemingColorChangedCallback);
    this.#fragmentList.registerHighlightingChangedCallback(this.#boundHighlightingChangedCallback);
    this.#fragmentList.registerModelMatrixChangedCallback(this.#boundModelMatrixChangedCallback);

    // Category highlighting is not supported by all model types, e.g., InstanceCollection2D, so we only register the
    // handlers if the feature is supported.
    this.#fragmentList.registerHighlightingMappingChangedCallback?.(this.#boundHighlightingCategoryChangedCallback);
  }

  #clearFragmentListCallbacks() {
    this.#fragmentList.removeMeshSetCallback(this.#boundMeshSetCallback);
    this.#fragmentList.removeTransformChangedCallback(this.#boundTransformChangedCallback);
    this.#fragmentList.removeMaterialChangedCallback(this.#boundMaterialChangedCallback);
    this.#fragmentList.removeThemingColorChangedCallback(this.#boundThemingColorChangedCallback);
    this.#fragmentList.removeHighlightingChangedCallback(this.#boundHighlightingChangedCallback);
    this.#fragmentList.removeModelMatrixChangedCallback(this.#boundModelMatrixChangedCallback);

    // If model supports category highlighting, deregister the callback
    this.#fragmentList.removeHighlightingCategoryChangedCallback?.(this.#boundHighlightingCategoryChangedCallback);
  }

  #clearRenderbatchCallbacks() {
    this.#forAllBatches((scene) => {
      scene.removeFragOrderChangedCallback(this.#boundFragOrderChangedCallback);
    });
  }

  /**
   * Sets and uploads the material reference uniform for the object at the specified index.
   * @param {number}         fragId     - The fragment to update.
   * @param {THREE.Material} material   - The material to associate with the fragment.
   * @param {boolean}        fromLoader - Hint about the origin of the material change - used for optimization (see below)
   */
  setMaterial(fragId, material, fromLoader) {
    this.#updateFragment(fragId, FragmentChangeType.Material, material);

    // No need to invalidate bundles during the load process. Batches won't be complete yet anyway, so we're not
    // using bundles, too.
    if (!fromLoader) {
      // We invalidate aggressively here. In many cases, invalidation might not actually be required. But if the
      // material is new, or if the fragments previously required a different shader (e.g. due to a different
      // texture configuration), we have to invalidate. It's just simpler to invalidate in all cases.
      this.#renderer.invalidateRenderBundles(this.#model);
    }
  }

  /**
   * @param {number} fragId - fragment to be updated
   */
  updateTransform(fragId) {
    this.#updateFragment(fragId, FragmentChangeType.Transform);
  }

  onModelMatrixChanged() {
    // Reupload all uniforms.
    this.#markScenesDirty();
  }

  /**
   * @param {number|undefined} fragId - fragment to be updated. If not provided, all fragments are updated.
   * @param {THREE.Vector4|undefined} color - new theming color. If not provided, theming is disabled.
   *
   * Update the theming color of a fragment or the whole model.
   */
  onThemingColorChanged(fragId, color) {
    if (fragId === undefined) {
      // Update all fragments.
      this.#markScenesDirty();
    } else {
      this.setThemingColor(fragId, color);
    }
  }

  /**
   * @param {number} fragId
   * @param {THREE.Vector4|undefined} color - new theming color. If not provided, theming is disabled.
   */
  setThemingColor(fragId, color) {
    this.#updateFragment(fragId, FragmentChangeType.Theming, color || this.#noThemingColor);
  }

  /**
   * @param {number} fragId
   */
  setHighlighting(fragId) {
    this.#updateFragment(fragId, FragmentChangeType.Highlighting);
  }

  // Update all uniforms of the given fragment
  setOneFragment(fragId) {
    this.#updateFragment(fragId, FragmentChangeType.Full);
  }

  // Called after uploading uniforms of a RenderBatch or if the fragment order of a RenderBatch changed.
  // Tracks for each fragment where to find the corresponding slot in the
  // UniformBuffer for later updates.
  updateFragmentLookup(
  renderBatch,
  rangeStart,
  count)
  {
    const cb = (fragId, index) => {
      if (fragId >= this.#fragIdToItemIndex.length) {
        // If the fragId is larger than the current size of the lookup table, we need to resize it.
        const newSize = (fragId + 1) * 1.5; // increase by 50%
        const newItemIndex = new Int32Array(newSize);
        newItemIndex.set(this.#fragIdToItemIndex);
        newItemIndex.fill(NotOnGPU, this.#fragIdToItemIndex.length, newSize);

        this.#fragIdToItemIndex = newItemIndex;
      }
      this.#fragIdToItemIndex[fragId] = rangeStart + index;
    };
    enumFragIds(renderBatch, count, cb);
  }

  // Call when removing this batch from GPU. Mark all fragments as "Not on GPU" again.
  removeFromFragmentLookup(
  renderBatch)
  {
    const cb = (fragId) => {
      this.#fragIdToItemIndex[fragId] = NotOnGPU;
    };
    enumFragIds(renderBatch, renderBatch.count, cb);
  }

  // Called in the render loop.
  //
  // If fragments are changed, we usually apply the updates immdediately, but track the number of such updates
  // between two consecutive frames. If they exceed a threshold value, we consider it as a "mass update", just
  // mark all scenes as dirty and update all uniforms in the next frame.
  resetUpdateHeuristic() {
    // TODO: For animations, this means that we will update the first MASS_UPDATE_THRESHOLD fragments individually
    // in the next iteration, before entering mass update mode again. Probably not a big deal, but we might want to
    // optimize this later.
    this.#updateCount = 0;
    this.#massUpdate = false;
  }

  registerRenderBatch(scene) {
    // Register callback for fragment order changes
    scene.registerFragOrderChangedCallback(this.#boundFragOrderChangedCallback);
    const range = this.#uniforms.getRange(scene);
    this.updateFragmentLookup(scene, range.startIndex, scene.count);
  }

  unregisterRenderBatch(scene) {
    // Remove callback for fragment order changes
    scene.removeFragOrderChangedCallback(this.#boundFragOrderChangedCallback);
    this.removeFromFragmentLookup(scene);
  }

  freeAllUniforms() {
    // Free all uniform buffers for RenderBatches of this model
    // Note that this will eventually done by OutOfCoreTileManager.
    // TODO: Above note suggest this can be removed now as OOC is always on for WebGPU.
    //       Investigate if this is true.
    this.#forAllBatches((scene) => {
      this.#uniforms.freeUniformBufferRange(scene);
    });
    return;
  }

  //
  // Private methods
  //

  /**
   * @param {number} fragId
   * @param {FragmentChangeType} type
   * @param {THREE.Vector4|THREE.Material} newValue - new value if provided. Only available for theming (Vector4) and Material.
   */
  #updateFragment(fragId, type, newValue) {
    if (this.#updateHeuristic()) {
      return;
    }

    // Find the gpu-side uniform buffer item that is used for this fragment
    const index = this.#fragIdToItemIndex[fragId];

    // Note:
    //  - (index === undefined) can only happen for dynamic models where fragment count may increase afterwards.
    //  - This means that a fragment has been added, but the batch that corresponds to it has not yet been uploaded to GPU.
    //  - In that case, the this.#fragIdToItemIndex has not yet been enlarged to accommodate the new fragment.
    //  - And therefore, the index is undefined, which implies that the fragment is not on GPU and thus should be treated as NotOnGPU.
    if (index === NotOnGPU || index === undefined) {
      return;
    }

    const batch = this.#iterator.getInstanceBatch(fragId);

    switch (type) {
      case FragmentChangeType.Material:
        this.#uniforms.setMaterial(newValue, batch, index);
        break;
      case FragmentChangeType.Transform:
        this.#uniforms.updateTransform(this.#fragmentList, fragId, batch, index);
        break;
      case FragmentChangeType.Theming:
        const color = this.#fragmentList.getThemingColor(fragId);
        this.#uniforms.setThemingColor(newValue, batch, index);
        break;
      case FragmentChangeType.Highlighting:
        const fragmentFlagsGPU = readFragmentFlagsGPU(this.#fragmentList, fragId);
        this.#uniforms.setFragmentFlags(fragmentFlagsGPU, batch, index);
        break;
      case FragmentChangeType.Full:
        this.#uniforms.setOneFragment(this.#fragmentList, fragId, batch, index);
        break;
    };
  }

  #updateHeuristic() {
    if (this.#massUpdate) {
      return true;
    }

    // If the number of uniform updates exceeds a threshold, we stop updating individual fragments
    // (in some cases, e.g. when updating animation transforms or theming colors).
    // Scenes are marked as dirty and uniforms will be updated in batches in the render loop.
    if (++this.#updateCount > MASS_UPDATE_THRESHOLD) {
      this.#massUpdate = true;
      this.#markScenesDirty();
      return true;
    }

    return false;
  }

  // Loop through all non-empty RenderBatches of the current model iterator
  #forAllBatches(cb) {
    const scenes = this.#iterator.getGeomScenes();
    let scene;
    for (let i = 0; i < scenes.length; ++i) {
      scene = scenes[i];

      // Skip missing or empty batches
      const empty = !scene || !scene.count;
      if (!empty) {
        cb(scene);
      }
    }
  }
  /**
   * Flags all scenes as dirty, so that they can be batch-updated when they are rendered the next time.
   */
  #markScenesDirty() {
    this.#forAllBatches((scene) => {
      scene.uniformsNeedUpdate = true;
    });
  }

  // Clears all data that was created for the previous iterator
  #unsetIterator() {

    if (!this.#iterator) {
      return;
    }

    this.freeAllUniforms();

    // Mark all fragments as "Not on GPU"
    this.#clearFragmentLookup();

    this.#forAllBatches((scene) => {

      // Remove callback for fragment order changes
      scene.removeFragOrderChangedCallback(this.#boundFragOrderChangedCallback);

      // Remove attached gpuRanges (will not be used for this batch anymore)
      this.#uniforms.clearRange(scene);
    });

    this.#iterator = null;
  }

  #iteratorChangedCallback(iterator) {

    // clear data associated with previous iterator first (if any)
    this.#unsetIterator();

    this.#iterator = iterator;
  }

  // Mark all fragments as "Not on GPU"
  #clearFragmentLookup() {
    this.#fragIdToItemIndex.fill(NotOnGPU);
  }

  /**
   * A callback that handles updates of the fragment order in a RenderBatch.
   */
  #fragOrderChangedCallback(start, count, renderBatch) {

    // Note: start and count are curently seem to be always the same for a RenderBatch anyway, so we actually don't need these params

    // TODO: Consider simplifying this function by...
    //   - Removing the "updateAll" case below (seem to never happen)
    //   - Using renderBatch as only param and substitute the other two by:
    //      - start = renderBatch.start
    //      - count = (renderBatch.lastItem - renderBatch.start)
    // Note that the count we get here is the range of "already added" fragments.
    // It's not always equal to renderBatch.count, but may be smaller during loading for ModelIteratorLinear.

    // If start is undefined, update all
    const updateAll = start === undefined;
    if (updateAll) {
      this.#clearFragmentLookup();
      this.#markScenesDirty();
      return;
    }

    //
    // For all fragments on GPU, we need to update the index where to find them in the GPU-side buffer
    // Note that the buffer itself doesn't change.
    const range = this.#uniforms.getRange(renderBatch);
    this.updateFragmentLookup(renderBatch, range.startIndex, count);

    // Update the actual uniform values
    // Why not just setting renderBatch.uniformsNeedUpdate to true?
    //     In some case, this callback might be triggered within forEachWebGPU(), which is done AFTER the uniformsNeedUpdate check
    //     in MainPass. Therefore, we have to update the uniforms directly - otherwise it would be one frame too late.
    this.#uniforms.updateRange(renderBatch, start, count);
  }
}

// Enumerate a range of fragments in a RenderBatch (always starting from 0)
const enumFragIds = (renderBatch, count, cb) => {
  const indices = renderBatch.getIndices();

  for (let i = 0; i < count; i++) {
    const index = renderBatch.start + i;
    const fragId = indices ? indices[index] : index;
    cb(fragId, i);
  }
};